// The side panel — 05i-2. main.js's hover drawer (open on the rail, close on leaving unless a
// drag from it is on), the rail click toggle, the outside-click close and the module search,
// copied exactly. The panel's markup is legacy-scaffold.html's (byte-identical); this only
// adds the listeners main.js added, installed before main.js loads, where main.js added them
// (its document click then still runs before the dropdowns' outside-click).

import { LIVE } from "../live/flag.js";
import { fuzzyMatches } from "../data/fuzzy-search.js";
import { isDraggingFromMenu } from "./menu-drag.js";
import { dismissWelcomeScreen } from "./welcome.js";

// Search-term expansions, e.g. 'btts' → 'both teams to score'. Emptied when Bento's old
// modules were stripped; the lookup stays so new ones only need adding here.
const SEARCH_ALIASES: Record<string, string> = {};

function byId(id: string): HTMLElement {
  const el = document.getElementById(id);
  if (!el) throw new Error(`#${id} missing from legacy-scaffold.html`);
  return el;
}

/** The words a search matches on, after any alias (an alias also matches from its first 3
 *  letters). Pure — main.js's runModuleSearch's first half. */
export function searchWords(raw: string, aliases: Record<string, string>): string[] {
  const aliasKey = aliases[raw] ? raw : raw.length >= 3 ? Object.keys(aliases).find((k) => k.startsWith(raw)) : undefined;
  const expanded = aliasKey ? aliases[aliasKey]! : raw;
  return expanded.split(/\s+/).filter(Boolean);
}

function installModuleSearch(sidePanel: HTMLElement): void {
  const input = byId("module-search") as HTMLInputElement;
  const clear = byId("module-search-clear");
  const results = byId("search-results");

  // Each group's tiles in their first order: a search MOVES matching tiles into the results
  // box (their drag listeners come along), so putting them back needs the original order.
  const origGridChildren = new Map<Element, Element[]>();
  sidePanel.querySelectorAll(".menu-grid").forEach((grid) => origGridChildren.set(grid, Array.from(grid.children)));

  function restoreModuleSearch(): void {
    results.querySelectorAll(".search-empty").forEach((el) => el.remove());
    results.classList.remove("active");
    // Appending a child that's already there moves it, so this rebuilds the original order.
    origGridChildren.forEach((children, grid) => children.forEach((child) => grid.appendChild(child)));
    sidePanel.querySelectorAll<HTMLElement>(".menu-title, .menu-grid").forEach((el) => (el.style.display = ""));
  }

  function runModuleSearch(): void {
    const raw = input.value.trim().toLowerCase();
    clear.classList.toggle("visible", raw.length > 0);
    restoreModuleSearch();
    if (!raw) return;

    const words = searchWords(raw, SEARCH_ALIASES);
    const tiles = LIVE ? ".menu-grid .module-template:not([data-snapshot-only])" : ".menu-grid .module-template:not([data-live-only])";
    const matches = Array.from(document.querySelectorAll<HTMLElement>(tiles)).filter((t) => {
      const title = t.dataset.title;
      if (title === undefined) throw new Error("module search: a .module-template without data-title");
      // Live: forgiving (accents, one typo — fuzzy-search.ts); the demo keeps its plain match.
      return words.every((w) => (LIVE ? fuzzyMatches(w, title) : title.toLowerCase().includes(w)));
    });
    sidePanel.querySelectorAll<HTMLElement>(".menu-title, .menu-grid").forEach((el) => (el.style.display = "none"));
    if (matches.length > 0) {
      matches.forEach((t) => results.appendChild(t));
    } else {
      const empty = document.createElement("div");
      empty.className = "search-empty";
      empty.textContent = "No matches";
      results.appendChild(empty);
    }
    results.classList.add("active");
  }

  input.addEventListener("input", runModuleSearch);
  clear.addEventListener("click", () => {
    input.value = "";
    runModuleSearch();
    input.focus();
  });
}

export function installSidePanel(): void {
  const hoverZone = byId("hover-zone");
  const sidePanel = byId("side-panel");

  hoverZone.addEventListener("mouseenter", () => {
    sidePanel.classList.add("open");
    dismissWelcomeScreen();
  });

  // Stays open while a tile / card is being dragged out of it.
  sidePanel.addEventListener("mouseleave", () => {
    if (!isDraggingFromMenu()) sidePanel.classList.remove("open");
  });

  hoverZone.addEventListener("click", (e) => {
    e.stopPropagation();
    sidePanel.classList.toggle("open");
    if (sidePanel.classList.contains("open")) dismissWelcomeScreen();
  });

  document.addEventListener("click", (e) => {
    const target = e.target as Node;
    if (sidePanel.classList.contains("open") && !sidePanel.contains(target) && !hoverZone.contains(target)) {
      sidePanel.classList.remove("open");
    }
  });

  installModuleSearch(sidePanel);
}
